import React, { useState, lazy } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface CollapsibleSectionProps {
  title: string;
  subtitle?: string;
  badge?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}
function CollapsibleSection({
  title,
  subtitle,
  badge,
  children,
  defaultOpen = false
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <section className="space-y-3" data-id="element-783">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between bg-white rounded-[1rem] md:rounded-[1.25rem] p-4 md:p-6 border border-black/5 hover:bg-black/[0.01] transition-colors text-left group" data-id="element-784">
        <div className="space-y-2 md:space-y-3" data-id="element-785">
          {badge && <span className="bg-[#ef3e23] text-white px-2 py-1 text-[9px] md:text-[10px] font-bold uppercase tracking-widest rounded-full w-fit block" data-id="element-786">
              {badge}
            </span>}
          <div className="space-y-1" data-id="element-787">
            <h2 className="font-display text-2xl md:text-4xl tracking-[-0.02em] leading-tight text-ink" data-id="element-788">
              {title}
            </h2>
            {subtitle && <p className="text-sm md:text-lg font-medium text-[#2b2b2b]/60 max-w-2xl leading-relaxed" data-id="element-789">
                {subtitle}
              </p>}
          </div>
        </div>
        <div className="p-1.5 md:p-2 rounded-full bg-black/5 group-hover:bg-black/10 transition-colors" data-id="element-790">
          {isOpen ? <ChevronUp className="w-5 h-5 md:w-6 md:h-6 text-ink" data-id="element-791" /> : <ChevronDown className="w-5 h-5 md:w-6 md:h-6 text-ink" data-id="element-792" />}
        </div>
      </button>

      <AnimatePresence data-id="element-793">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-794">
            <div className="bg-white rounded-[1rem] md:rounded-[1.25rem] p-4 md:p-6 border border-black/5" data-id="element-795">
              {children}
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}
function SubCollapsibleSection({
  title,
  children,
  defaultOpen = false
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <div className="space-y-4" data-id="element-796">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between bg-black/[0.02] rounded-xl p-4 md:p-6 border border-black/5 hover:bg-black/[0.04] transition-colors text-left group" data-id="element-797">
        <h3 className="text-[10px] md:text-sm font-bold uppercase tracking-widest text-[#2b2b2b]/60" data-id="element-798">
          {title}
        </h3>
        <div className="p-1.5 rounded-full bg-black/5 group-hover:bg-black/10 transition-colors" data-id="element-799">
          {isOpen ? <ChevronUp className="w-4 h-4 md:w-5 md:h-5 text-ink" data-id="element-800" /> : <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-ink" data-id="element-801" />}
        </div>
      </button>

      <AnimatePresence data-id="element-802">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-803">
            <div className="pt-2 pb-4 space-y-6 md:space-y-8" data-id="element-804">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/ehsyfSXNodvptu8aWDpnAi/headway-logo-rgb-black.png';
const PHOTO_URL = 'https://cdn.magicpatterns.com/uploads/jeJD8qmBPvi7QCy91UYe5N/Screenshot_2026-06-12_at_22.02.07.png';
export function VisualLanguage() {
  const signatureHtmlOptionOne = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; color: #000000; line-height: 1.4; max-width: 650px; width: 100%;">
  <tr>
    <td valign="top" style="padding-bottom: 24px;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td width="45%" valign="top" class="email-sig-stack" style="padding-right: 32px;">
            <img src="${LOGO_URL}" alt="Headway Tuition" width="200" style="display: block; border: 0; margin-bottom: 16px;" />
            <div style="font-size: 11px; color: #000000; line-height: 1.4; margin-bottom: 32px;">
              <strong>Headway Tuition Limited</strong><br />
              213 Station Road,<br /><br />Birmingham, B33 8BB
            </div>
            <div style="font-size: 15px; color: #FC7010; margin-bottom: 24px; line-height: 1.3;">
              <span style="color: #FC7010;">The pathway for</span><br />
              <span style="color: #FC7010;"><strong>year 3 to year 6</strong></span><br />
              <span style="color: #FC7010;">for <strong>11+ exams.</strong></span>
            </div>
            <div style="font-size: 15px; font-weight: bold; color: #000000; line-height: 1.1;">
              headwaytuition<br />.co.uk
            </div>
          </td>
          <td width="1" class="email-sig-divider" style="border-left: 1px solid #e0e0e0;"></td>
          <td width="55%" valign="top" class="email-sig-stack" style="padding-left: 40px;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 24px;">
              <tr>
                <td width="110" valign="top" class="email-sig-stack email-sig-photo" style="padding-right: 20px;">
                  <img src="${PHOTO_URL}" alt="Anita Oberoi" width="110" height="110" style="display: block; border-radius: 50%; border: 0; object-fit: cover;" />
                </td>
                <td valign="middle" class="email-sig-stack" style="padding-top: 4px;">
                  <div style="font-size: 17px; color: #000000; margin-bottom: 4px; line-height: 1.2;">
                    <strong>Anita Oberoi</strong><br />BA (Hons), PGCE, QTS
                  </div>
                  <div style="font-size: 14px; color: #9E00E8; font-weight: bold;">Director</div>
                </td>
              </tr>
            </table>
            <table cellpadding="0" cellspacing="0" border="0" style="margin-bottom: 28px;">
              <tr>
                <td style="padding-bottom: 6px; font-size: 14px;">
                  <span style="color: #9E00E8; font-weight: bold; display: inline-block; width: 28px;">tel.</span>
                  <a href="tel:+4407366947574" style="color: #000000; text-decoration: none;">+44 (0) 7366 947574</a>
                </td>
              </tr>
              <tr>
                <td style="padding-bottom: 6px; font-size: 14px;">
                  <span style="color: #9E00E8; font-weight: bold; display: inline-block; width: 28px;">in.</span>
                  <a href="https://www.linkedin.com/in/anita-oberoi/" target="_blank" rel="noopener noreferrer" style="color: #000000; text-decoration: none;">Anita Oberoi</a>
                </td>
              </tr>
              <tr>
                <td style="font-size: 14px;">
                  <span style="color: #9E00E8; font-weight: bold; display: inline-block; width: 28px;">fb.</span>
                  <a href="https://www.facebook.com/anitaoberoiheadwaytuition/" target="_blank" rel="noopener noreferrer" style="color: #000000; text-decoration: none;">Headway Tuition</a>
                </td>
              </tr>
            </table>
            <a href="#" style="display: inline-block; background-color: #9E00E8; color: #ffffff; padding: 12px 36px; border-radius: 30px; font-weight: bold; text-decoration: none; font-size: 14px; text-align: center; box-shadow: 0 4px 12px rgba(158, 0, 232, 0.2);">Book a call</a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding-top: 16px; border-top: 1px solid #cccccc;">
      <div style="font-size: 8px; color: #666666; line-height: 1.4; text-align: justify;">
        The content of this email and any attachments transmitted with it are confidential. It may also be legally privileged or otherwise protected from disclosure. This email should not be used by anyone who is not an original intended recipient. If you have received this email by mistake please notify us by emailing the sender, and then delete the email and any copies from your system.
      </div>
    </td>
  </tr>
</table>
  `;
  const signatureHtmlOptionTwo = `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; color: #000000; line-height: 1.4; max-width: 750px; width: 100%;">
  <tr>
    <td valign="top" style="padding-bottom: 24px;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td width="48%" valign="top" class="email-sig-stack" style="padding-right: 32px;">
            <img src="${LOGO_URL}" alt="Headway Tuition" width="220" style="display: block; border: 0; margin-bottom: 32px;" />
            <div style="font-size: 16px; color: #FC7010; margin-bottom: 48px; line-height: 1.3;">
              The pathway for<br />year 3 to year 6<br />for <strong>11+ exams.</strong>
            </div>
            <img src="https://cdn.magicpatterns.com/uploads/9W1V4vqaNbgDyXw5hsjjF4/Screenshot_2026-06-12_at_22.18.14.png" alt="Brand Graphics" width="260" style="display: block; border: 0; margin-bottom: 16px;" />
            <div style="font-size: 20px; font-weight: bold; color: #000000; line-height: 1.1;">
              headwaytuition<br />.co.uk
            </div>
          </td>
          <td width="1" class="email-sig-divider" style="border-left: 1px solid #e0e0e0;"></td>
          <td width="52%" valign="top" class="email-sig-stack" style="padding-left: 40px;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td width="100" valign="top" class="email-sig-stack email-sig-photo" style="padding-right: 20px;">
                  <img src="${PHOTO_URL}" alt="Anita Oberoi" width="100" height="100" style="display: block; border-radius: 50%; border: 0; object-fit: cover;" />
                </td>
                <td valign="top" class="email-sig-stack">
                  <div style="font-size: 18px; color: #000000; margin-bottom: 4px; line-height: 1.2;">
                    <strong>Anita Oberoi</strong><br />BA (Hons), PGCE, QTS
                  </div>
                  <div style="font-size: 16px; color: #666666; margin-bottom: 24px;">Director</div>
                  <div style="font-size: 16px; color: #000000; line-height: 1.5;">
                    <span style="color: #9E00E8; font-weight: 500;">Tel.</span> +44 (0) 7366 947574<br />
                    <span style="color: #9E00E8; font-weight: 500;">Facebook</span> <a href="https://www.facebook.com/anitaoberoiheadwaytuition/" target="_blank" rel="noopener noreferrer" style="color: #000000; text-decoration: none;">Anita Oberoi</a><br />
                    <span style="color: #9E00E8; font-weight: 500;">Linkedin</span> <a href="https://www.linkedin.com/in/anita-oberoi/" target="_blank" rel="noopener noreferrer" style="color: #000000; text-decoration: none;">Headway Tuition</a><br />
                    <div style="margin-top: 12px; margin-bottom: 20px;">
                      <strong>Headway Tuition Limited</strong><br />213 Station Road,<br />Birmingham, B33 8BB
                    </div>
                    <a href="#" style="display: inline-block; background-color: #9E00E8; color: #ffffff; padding: 12px 48px; border-radius: 30px; font-weight: bold; text-decoration: none; font-size: 16px; text-align: center; box-shadow: 0 4px 12px rgba(158, 0, 232, 0.2);">Book a call</a>
                  </div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding-top: 16px; border-top: 1px solid #cccccc;">
      <div style="font-size: 8px; color: #666666; line-height: 1.4; text-align: justify;">
        The content of this email and any attachments transmitted with it are confidential. It may also be legally privileged or otherwise protected from disclosure. This email should not be used by anyone who is not an original intended recipient. If you have received this email by mistake please notify us by emailing the sender, and then delete the email and any copies from your system.
      </div>
    </td>
  </tr>
</table>
  `;
  return <div className="min-h-screen bg-[#d9e2e8] p-2 md:p-4 font-sans selection:bg-accent selection:text-white" data-id="element-805">
      <style dangerouslySetInnerHTML={{
      __html: `
        @media (max-width: 768px) {
          .email-sig-stack {
            display: block !important;
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            box-sizing: border-box !important;
          }
          .email-sig-divider{
            display: block !important;
            width: 100% !important;
            border-left: none !important;
            border-top: 1px solid #e0e0e0 !important;
            height: 1px !important;
            margin: 24px 0 !important;
          }
          .email-sig-photo {
            margin-bottom: 16px !important;
          }
        }
      `
    }} data-id="element-806" />
      <div className="main-container max-w-[1600px] mx-auto bg-card rounded-[1.5rem] md:rounded-[2rem] border border-black/5 shadow-sm min-h-[calc(100vh-2rem)] flex flex-col" data-id="element-807">
        <Navigation activePath="/visuallanguage" data-id="element-808" />
        <main className="flex-1 px-4 md:px-16 py-8 space-y-12 md:space-y-32" data-id="element-809">
          {/* Hero Section */}
          <section className="relative flex flex-col justify-between pt-4 md:pt-8 pb-8 md:pb-12" data-id="element-810">
            <div className="max-w-6xl" data-id="element-811">
              <h1 className="text-[clamp(2.5rem,8vw,8rem)] font-semibold leading-[0.95] md:leading-[0.9] tracking-[-0.04em] text-ink" data-id="element-812">
                Visual <br data-id="element-813" />
                Language
                <span className="text-accent inline-block w-[0.15em] h-[0.15em] rounded-full ml-[0.05em] mb-[0.1em]" data-id="element-814" />
              </h1>
              <div className="mt-6 max-w-sm text-base md:text-lg leading-[1.6] md:leading-[1.7] text-ink/70 font-medium" data-id="element-815">
                The building blocks of the Headway Tuition brand: geometry,
                patterns, and meaning.
              </div>
            </div>
          </section>

          {/* Geometry & Meaning Section */}
          <CollapsibleSection title="Geometry & Meaning" badge="Graphic System" subtitle="The building blocks of the Headway Tuition brand: geometry, patterns, and meaning." data-id="element-816">
            <div className="space-y-10 md:space-y-16" data-id="element-817">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center" data-id="element-818">
                <div className="grid grid-cols-2 gap-6 md:gap-12" data-id="element-819">
                  <div className="space-y-3 md:space-y-4" data-id="element-820">
                    <div className="w-16 h-16 md:w-24 md:h-24 border-2 md:border-4 border-ink rounded-full" data-id="element-821" />
                    <p className="text-xs md:text-sm font-medium text-ink/60" data-id="element-822">
                      Calm mind,
                      <br data-id="element-823" />
                      steady results
                    </p>
                  </div>
                  <div className="space-y-3 md:space-y-4" data-id="element-824">
                    <div className="w-16 h-16 md:w-24 md:h-24 border-2 md:border-4 border-ink overflow-hidden" data-id="element-825">
                      <div className="w-full h-full bg-ink origin-bottom-left -rotate-45 scale-[1.5]" data-id="element-826" />
                    </div>
                    <p className="text-xs md:text-sm font-medium text-ink/60" data-id="element-827">
                      Strong,
                      <br data-id="element-828" />
                      self reliant learners
                    </p>
                  </div>
                  <div className="space-y-3 md:space-y-4" data-id="element-829">
                    <div className="w-8 h-16 md:w-12 md:h-24 border-2 md:border-4 border-ink" data-id="element-830" />
                    <p className="text-xs md:text-sm font-medium text-ink/60" data-id="element-831">
                      Parents and tutor
                      <br data-id="element-832" />
                      working together
                    </p>
                  </div>
                  <div className="space-y-3 md:space-y-4" data-id="element-833">
                    <div className="w-16 h-8 md:w-24 md:h-12 border-2 md:border-4 border-ink rounded-t-full" data-id="element-834" />
                    <p className="text-xs md:text-sm font-medium text-ink/60" data-id="element-835">
                      Safe,
                      <br data-id="element-836" />
                      small groups that build confidence
                    </p>
                  </div>
                </div>
                <div className="rounded-[0.75rem] md:rounded-[1.5rem] overflow-hidden border border-black/5 bg-white shadow-sm" data-id="element-837">
                  <img src="https://cdn.magicpatterns.com/uploads/idU1xjQuhwKB5fLJHMEQKV/Screenshot_2026-06-05_at_12.13.14.png" alt="Visual Language Patterns" className="w-full h-auto block" loading="lazy" data-id="element-838" />
                </div>
              </div>
              <div className="pt-10 md:pt-16 border-t border-border space-y-8" data-id="element-839">
                <div className="rounded-[0.75rem] md:rounded-[1.5rem] overflow-hidden border border-black/5 bg-white shadow-sm" data-id="element-840">
                  <img src="https://cdn.magicpatterns.com/uploads/38hzJVimYi2SBmYhcrHBLS/shapes2.png" alt="Brand Shape Composition" className="w-full h-auto block" loading="lazy" data-id="element-841" />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Logo Animation Section */}
          <CollapsibleSection title="Logo Animation" badge="Motion" data-id="element-842">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-843">
              <div className="rounded-[0.75rem] md:rounded-[1.5rem] overflow-hidden border border-black/5 bg-white shadow-sm flex items-center justify-center p-8 md:p-12" data-id="element-844">
                <video src="https://cdn.magicpatterns.com/uploads/i2CHqJUdmyLFStpkce7fdD/headway-logo-animation-text-1.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-845" />
              </div>
              <div className="rounded-[0.75rem] md:rounded-[1.5rem] overflow-hidden border border-black/5 bg-white shadow-sm flex items-center justify-center p-8 md:p-12" data-id="element-846">
                <video src="https://cdn.magicpatterns.com/uploads/kYogtKKqdGS7Sem7QeZEeW/headway-logo-animation-icon-2.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-847" />
              </div>
            </div>
          </CollapsibleSection>

          {/* Posters Section */}
          <CollapsibleSection title="Posters" badge="Physical Assets" data-id="element-848">
            <div className="space-y-8" data-id="element-849">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-850">
                <div className="rounded-[0.75rem] md:rounded-[1.5rem] overflow-hidden border border-black/5 bg-white shadow-sm flex items-center justify-center p-4" data-id="element-851">
                  <img src="https://cdn.magicpatterns.com/uploads/gb7hUfPWg3Brxa5PszwbSF/a1-poster1.png" alt="Poster Option 1" className="max-h-[400px] w-auto object-contain" data-id="element-852" />
                </div>
                <div className="rounded-[0.75rem] md:rounded-[1.5rem] overflow-hidden border border-black/5 bg-white shadow-sm flex items-center justify-center p-4" data-id="element-853">
                  <img src="https://cdn.magicpatterns.com/uploads/5LH62iupnFmmpeh1imFJaY/a1-poster2.png" alt="Poster Option 2" className="max-h-[400px] w-auto object-contain" data-id="element-854" />
                </div>
              </div>
              <p className="text-base md:text-2xl font-medium text-[#2b2b2b]/60 max-w-2xl leading-relaxed" data-id="element-855">
                Final production files can be found in the{' '}
                <a href="/final-assets" className="text-accent hover:underline font-bold" data-id="element-856">
                  Final Assets
                </a>{' '}
                section.
              </p>
            </div>
          </CollapsibleSection>

          {/* Roll Up Banners Section */}
          <CollapsibleSection title="Roll Up Banners" badge="Physical Assets" subtitle="High-impact banners designed for events and tutors' locations." data-id="element-857">
            <p className="text-base md:text-2xl font-medium text-[#2b2b2b]/60 max-w-2xl leading-relaxed" data-id="element-858">
              Final production files can be found in the{' '}
              <a href="/final-assets" className="text-accent hover:underline font-bold" data-id="element-859">
                Final Assets
              </a>{' '}
              section.
            </p>
          </CollapsibleSection>

          {/* Linkedin Banners Section */}
          <CollapsibleSection title="Linkedin Banners" badge="Digital Assets" data-id="element-860">
            <div className="space-y-16 md:space-y-24" data-id="element-861">
              {/* Personal Banners */}
              <div className="space-y-10" data-id="element-862">
                <div className="flex items-center gap-4" data-id="element-863">
                  <h3 className="text-xl md:text-2xl font-bold text-ink tracking-tight" data-id="element-864">
                    Personal Banners
                  </h3>
                  <div className="h-px flex-1 bg-black/5" data-id="element-865" />
                </div>

                <div className="space-y-12" data-id="element-866">
                  <div className="space-y-4" data-id="element-867">
                    <h4 className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-accent px-2" data-id="element-868">
                      Approved LinkedIn Banner
                    </h4>
                    <div className="max-w-5xl" data-id="element-869">
                      <div className="overflow-hidden border border-black/5 bg-white shadow-sm" data-id="element-870">
                        <img src="https://cdn.magicpatterns.com/uploads/9nfTu9NtbZrwRdYTsHFMjE/headway-linkedin-personal-banner.png" alt="Approved Personal Banner" className="w-full h-auto block" loading="lazy" data-id="element-871" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Banners */}
              <div className="space-y-10" data-id="element-872">
                <div className="flex items-center gap-4" data-id="element-873">
                  <h3 className="text-xl md:text-2xl font-bold text-ink tracking-tight" data-id="element-874">
                    Company Banners
                  </h3>
                  <div className="h-px flex-1 bg-black/5" data-id="element-875" />
                </div>

                <div className="space-y-12" data-id="element-876">
                  <div className="space-y-4" data-id="element-877">
                    <h4 className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-accent px-2" data-id="element-878">
                      Approved LinkedIn Banner
                    </h4>
                    <div className="max-w-5xl" data-id="element-879">
                      <div className="overflow-hidden border border-black/5 bg-white shadow-sm" data-id="element-880">
                        <img src="https://cdn.magicpatterns.com/uploads/aB3uctBcNzQFYvLxwcNzQw/headway-linkedin-company-banner.png" alt="Approved Company Banner" className="w-full h-auto block" loading="lazy" data-id="element-881" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-base md:text-2xl font-medium text-[#2b2b2b]/60 max-w-2xl leading-relaxed" data-id="element-882">
                Final artwork files can be found within the{' '}
                <a href="/final-assets" className="text-accent hover:underline font-bold" data-id="element-883">
                  Final Assets
                </a>{' '}
                section.
              </p>
            </div>
          </CollapsibleSection>

          {/* Social Media Posts Section */}
          <CollapsibleSection title="Social Media Posts" badge="Digital Assets" data-id="element-884">
            <div className="space-y-8" data-id="element-885">
              <div className="rounded-[0.75rem] md:rounded-[1.5rem] border border-black/5 bg-white shadow-sm overflow-hidden h-[300px] md:h-[400px] relative group cursor-ns-resize" data-id="element-886">
                <img src="https://cdn.magicpatterns.com/uploads/8Ee5CTiidf7S6BseaGQ685/Headway_Tuition_SocialPosts.png" alt="Social Media Posts Grid" className="w-full h-auto block transition-transform duration-[8s] ease-in-out group-hover:-translate-y-[calc(100%-300px)] md:group-hover:-translate-y-[calc(100%-400px)]" loading="lazy" data-id="element-887" />
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none group-hover:opacity-0 transition-opacity" data-id="element-888" />
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-889">
                  Hover to scroll
                </div>
              </div>
              <p className="text-base md:text-2xl font-medium text-[#2b2b2b]/60 max-w-2xl leading-relaxed" data-id="element-890">
                Final artwork files can be found within the{' '}
                <a href="/final-assets" className="text-accent hover:underline font-bold" data-id="element-891">
                  Final Assets
                </a>{' '}
                section.
              </p>
            </div>
          </CollapsibleSection>

          {/* Email Signatures Section */}
          <CollapsibleSection title="Email Signatures" badge="Digital Assets" data-id="element-892">
            <div className="space-y-12" data-id="element-893">
              <div className="flex items-center gap-4" data-id="element-894">
                <h2 className="text-2xl md:text-3xl font-bold text-ink tracking-tight" data-id="element-895">
                  Approved Email Signature
                </h2>
                <div className="h-px flex-1 bg-black/5" data-id="element-896" />
              </div>

              <div className="max-w-2xl space-y-8" data-id="element-897">
                <div className="space-y-4" data-id="element-898">
                  <div className="bg-white rounded-[1rem] p-4 border border-black/5 shadow-sm overflow-hidden" data-id="element-899">
                    <img src="https://cdn.magicpatterns.com/uploads/74UXWz1QoXvoBT662Lvhhi/headway-email-signature-2.png" alt="Approved Email Signature" className="w-full h-auto block" loading="lazy" data-id="element-900" />
                  </div>
                </div>
                <p className="text-base md:text-2xl font-medium text-[#2b2b2b]/60 max-w-2xl leading-relaxed" data-id="element-901">
                  Final artwork files can be found within the{' '}
                  <a href="/final-assets" className="text-accent hover:underline font-bold" data-id="element-902">
                    Final Assets
                  </a>{' '}
                  section.
                </p>
              </div>
            </div>
          </CollapsibleSection>

          {/* Letter Head Section */}
          <CollapsibleSection title="Letter Head" badge="Print Assets" data-id="element-903">
            <div className="space-y-12" data-id="element-904">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16" data-id="element-905">
                {[{
                src: 'https://cdn.magicpatterns.com/uploads/tCr5F8f9c34qHNv68mGe8a/headway-letterhead-3.png',
                label: 'Option 3 (Approved)'
              }].map((item, i) => <div key={i} className="space-y-6" data-id="element-906">
                    <div className="bg-white border border-black/10 shadow-2xl shadow-black/5 overflow-hidden aspect-[1/1.414] relative group" data-id="element-907">
                      <img src={item.src} alt={item.label} className="w-full h-full object-contain block" loading="lazy" data-id="element-908" />
                      <div className="absolute inset-0 border border-black/5 pointer-events-none" data-id="element-909" />
                    </div>
                    <div className="flex items-center justify-between px-1" data-id="element-910">
                      <p className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-accent" data-id="element-911">
                        {item.label}
                      </p>
                      <p className="text-[8px] md:text-[10px] font-medium text-ink/40 uppercase tracking-tighter" data-id="element-912">
                        A4 Format • 210 × 297 mm
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </CollapsibleSection>

          {/* Booklet Section */}
          <CollapsibleSection title="Booklet" badge="Print Assets" data-id="element-913">
            <div className="space-y-8 md:space-y-12" data-id="element-914">
              <div className="space-y-6 md:space-y-8" data-id="element-915">
                <div className="rounded-[0.75rem] md:rounded-[1.5rem] overflow-hidden border border-black/5 bg-white shadow-sm" data-id="element-916">
                  <img src="https://cdn.magicpatterns.com/uploads/tXiudMLpBfkhR28ktaBjSQ/Headway_Tuition_Mockup.png" alt="Approved Booklet Mockup" className="w-full h-auto block" loading="lazy" data-id="element-917" />
                </div>
                <p className="text-base md:text-2xl font-medium text-[#2b2b2b]/60 max-w-2xl leading-relaxed" data-id="element-918">
                  Final production and digital files can be found in the{' '}
                  <a href="/final-assets" className="text-accent hover:underline font-bold" data-id="element-919">
                    Final Assets
                  </a>{' '}
                  section.
                </p>
              </div>
            </div>
          </CollapsibleSection>
        </main>
        <Footer data-id="element-920" />
      </div>
    </div>;
}